import take from 'lodash/take';
import moment from 'moment-timezone';
import store from '@/store';
import '@/constants/conversation/locales';

export const findDispositionCodeById = (list, id) => {
  let dispositionCode = null;

  function findDispositionCodeInTree(list, id) {
    for (let i = 0; i < list.length; i++) {
      const code = list[i];
      if (code.id === id) {
        dispositionCode = code;
        break;
      } else if (code.children && code.children.length) {
        findDispositionCodeInTree(code.children, id);
      }
    }
  }

  findDispositionCodeInTree(list, id);

  return dispositionCode;
};

export const traverseDispositionCodesObject = obj => {
  const nodes = [];

  function formatDispositionCodes(list) {
    return list.map(({ id, name }) => ({ id, label: name }));
  }

  if (
    Object.keys(obj).length === 1 &&
    obj.hasOwnProperty('Disposition Code Category 1') &&
    Object.keys(obj['Disposition Code Category 1']).length === 1 &&
    obj['Disposition Code Category 1'].hasOwnProperty('Disposition Code Sub Category 1')
  ) {
    return formatDispositionCodes(obj['Disposition Code Category 1']['Disposition Code Sub Category 1']);
  } else if (obj) {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (obj[key] instanceof Array) {
          nodes.push({
            id: `id-${key}-${(Math.random() * 1e7) | 0}`,
            label: key,
            children: formatDispositionCodes(obj[key])
          });
        } else {
          nodes.push({
            id: `id-${key}`,
            label: key,
            children: traverseDispositionCodesObject(obj[key])
          });
        }
      }
    }
  }
  return nodes;
};

export const getPathFromTreeByNodeId = (tree, id) => {
  let result = null;

  function findNodeInTree(tree, id) {
    const nodes = tree.children;
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const path = tree.path == null ? node.label : `${tree.path} / ${node.label}`;
      const children = node.children;

      if (node.id === id) {
        result = path;
        break;
      } else if (children && children.length) {
        findNodeInTree({ path, children }, id);
      }
    }
  }

  findNodeInTree({ path: null, children: tree }, id);

  return result;
};

export const removeItemsFromTree = (tree, itemsToRemove) => {
  for (let i = 0, len = tree.length; i < len; i++) {
    const currentNode = tree[i] || {};
    const children = currentNode.children;
    const index = itemsToRemove.findIndex(item => item.id === currentNode.id);
    if (index >= 0) {
      tree.splice(i, 1);
      i--;
    } else if (children && children.length) {
      removeItemsFromTree(children, itemsToRemove);
    }
  }
};

export const sortByUsage = (item1, item2) => {
  const usage1 = item1.usages;
  const usage2 = item2.usages;
  return usage1 > usage2 ? -1 : 1;
};

export const parseMessage = message => {
  const result = [];
  message.split(' ').forEach(textPiece => {
    const cleanTextPiece = replaceHtmlEntities(textPiece);
    if (isUrl(cleanTextPiece)) {
      const urlWithSchema = cleanTextPiece.startsWith('http') ? cleanTextPiece : `http://${cleanTextPiece}`;
      result.push(`<a href="${urlWithSchema}" target="_blank">${cleanTextPiece}</a>`);
    } else {
      result.push(cleanTextPiece);
    }
  });
  return result.join(' ');
};

const replaceHtmlEntities = text => {
  // eslint-disable-next-line
  return text.replace(/[\u00A0-\u9999<>\&]/gim, i => `&#${i.charCodeAt(0)};`);
};

export const splitCamelCasedWords = text => {
  return text
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/(\d+)/g, ' $1 ')
    .trim();
};

const isUrl = text => {
  const urlRegex =
    '^(?!mailto:)(?:(?:http|https|ftp)://)(?:\\S+(?::\\S*)?@)?(?:(?:(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}(?:\\.(?:[0-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))|(?:(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)(?:\\.(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)*(?:\\.(?:[a-z\\u00a1-\\uffff]{2,})))|localhost)(?::\\d{2,5})?(?:(/|\\?|#)[^\\s]*)?$';
  const url = new RegExp(urlRegex, 'i');
  return text.length < 2083 && url.test(text);
};

const isSupervisor = (agent, supervisors) => supervisors.some(supervisor => supervisor.id === agent.id);

export const filterSupervisorsFromAgents = (agents, supervisors) =>
  agents.filter(agent => !isSupervisor(agent, supervisors));

export const filterAgentById = (agents, agentIdToFilter) => agents.filter(agent => agent.id !== agentIdToFilter);

export const sortByName = (c1, c2) => {
  const fallbackSort = c1.name < c2.name ? -1 : 0;
  return c1.name > c2.name ? 1 : fallbackSort;
};

export const openExternalLinks = (links = [], maxLinks = 20, target = '_blank') => {
  /**
   * linksToOpen is set to max due to open a lot of tabs(e.g. 1000) would consume a lot of RAM on agent browser
   * and possible kill the process.
   */
  const linksToOpen = take(links, maxLinks);
  linksToOpen.forEach(link => {
    window.open(link, target);
  });
};

function downloadFile(content, mediaType) {
  const url = window.URL.createObjectURL(new Blob([content]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', mediaType + '.txt');
  link.setAttribute('download', mediaType + '.txt');
  document.body.appendChild(link);
  link.click();
}

export const downloadConversation = (customer, conversation, mediaType) => {
  const enter = '\r\n';
  const messages = conversation.messages;
  const agent = conversation.agent;
  let fileContent = mediaType + ' Information';
  const separator = '===============================';
  fileContent = fileContent.concat(enter, separator);
  fileContent = addCustomerInformation(customer, fileContent, mediaType);
  fileContent = fileContent.concat(enter, `Conversation with ${agent}`);
  fileContent = fileContent.concat(enter, separator);
  fileContent = addMessagesToFile(messages, fileContent, customer, conversation.agent);
  downloadFile(fileContent, mediaType);
};

function addCustomerInformation(customer, fileContent, mediaType) {
  const enter = '\r\n';
  let content = fileContent;
  if (mediaType === 'SMS') {
    content = content.concat(enter, `Phone #: ${customer.email}`);
  } else if (mediaType === 'OUT. EXTERNAL MAIL' || mediaType === 'INCOMING EMAIL') {
    content = content.concat(enter, `Email: ${customer.email}`);
  } else if (mediaType === 'CHAT') {
    content = content.concat(enter, `Name: ${customer.name}`);
    content = content.concat(enter, `Email: ${customer.email}`);
  }
  if (customer.customDate) {
    content = content.concat(enter, `Custom Date: ${customer.customDate}`);
  }
  if (customer.customNumber) {
    content = content.concat(enter, `Custom Number: ${customer.customNumber}`);
  }
  return content;
}

function addMessagesToFile(messages, fileContent, customer, agent) {
  const { timeFormat } = store.getters['SETTINGS/applicationSettings'];
  const enter = '\r\n';
  let content = fileContent;

  let replaceCustomerEmailWithName = false;
  if (customer.name && customer.email) {
    replaceCustomerEmailWithName = customer.name !== customer.email;
  }

  messages.forEach(message => {
    const messageName = message.originator ? message.originator.name : message.from;
    if (!messageName && !message.content) {
      return;
    }
    const time = moment(message.timestamp, timeFormat, true);
    let timestamp = '';
    // Time only
    if (time.isValid()) {
      timestamp = time.format(timeFormat);
    } else {
      timestamp = moment(message.timestamp).format(timeFormat);
    }

    let originator = messageName;
    if (replaceCustomerEmailWithName && messageName === customer.email) {
      originator = customer.name;
    } else if (message.to && message.from !== customer.email) {
      originator = agent;
    }

    content = content.concat(enter, `(${timestamp}) ${originator}: ${message.content}`);
  });

  return content;
}

export const downloadEmails = (customer, emails, mediaType) => {
  const enter = '\r\n';
  let fileContent = 'Email Information';
  const separator = '===============================';
  const agent = mediaType === 'INCOMING EMAIL' ? emails[0].from : customer.name;
  fileContent = fileContent.concat(enter, separator);
  fileContent = addCustomerInformation(customer, fileContent, mediaType);
  fileContent = fileContent.concat(enter, `Conversation with ${agent}`);
  fileContent = fileContent.concat(enter, separator);
  fileContent = addEmailsToFile(emails, fileContent, mediaType);
  downloadFile(fileContent, mediaType);
};

function addEmailsToFile(emails, fileContent, mediaType) {
  const { timeFormat } = store.getters['SETTINGS/applicationSettings'];
  const enter = '\r\n';
  const separator = '===============================';
  let content = fileContent;

  emails.forEach((email, index) => {
    let emailContent = email.body.trim().replace(/(<br>)/g, enter);
    emailContent = emailContent.replace(/(<\/[^>]>)/g, enter);
    emailContent = emailContent.replace(/<\/?("[^"]*"|'[^']*'|[^>])*(>|$)/g, '');
    const isIncomingEmail = mediaType === 'INCOMING EMAIL';
    let originator = isIncomingEmail ? 'INCOMING EMAIL' : 'OUTGOING EMAIL';
    if (index) {
      originator = isIncomingEmail ? 'OUTGOING EMAIL' : 'INCOMING EMAIL';
    }

    const time = moment(email.creationTime, timeFormat, true);
    let timestamp = '';
    // Time only
    if (time.isValid()) {
      timestamp = time.format(timeFormat);
    } else {
      timestamp = moment(email.creationTime).format(timeFormat);
    }

    content = content.concat(enter, `${originator}`);
    content = content.concat(enter, `${timestamp} - ${email.subject}`);
    content = content.concat(enter, `${emailContent}`, enter, separator);
  });

  return content;
}
